import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Container, Grid } from '@mui/material';
import { NurseStatusSection, PatientStatusSection, TalkRoomStatusSection } from '../components/dashboard';
import { 
  DashboardMetricsResponse, 
  fetchDashboardMetrics, 
  updateDashboardMetric 
} from '../api/dashboard';
import { notifyAction } from '../components/common/ActionNotifier';

const Dashboard: React.FC = () => {
  const [ loading, setLoading ] = useState<boolean>(true);
  const [ updatingMetric, setUpdatingMetric ] = useState<string | null>(null);
  const [ metrics, setMetrics ] = useState<DashboardMetricsResponse | null>(null);
  const [ error, setError ] = useState<string | null>(null);
  const [ lastUpdated, setLastUpdated ] = useState<string | null>(null);
  
  const fetchMetrics = async() => {
    setLoading(true);
    try {
      const data = await fetchDashboardMetrics();
      setMetrics(data);
      setLastUpdated(new Date().toISOString());
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError('ダッシュボードメトリクスの取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMetrics();
  }, []);
  
  // 全体のリフレッシュを行う
  const handleRefresh = () => {
    fetchMetrics();
  };
  
  // 特定のメトリクスセクションを更新する
  const handleUpdateMetric = async (metricKey: string) => {
    setUpdatingMetric(metricKey);
    try {
      const updatedData = await updateDashboardMetric(metricKey);
      setMetrics(updatedData);
      setLastUpdated(new Date().toISOString());
      
      // 共通の通知コンポーネントを使用して成功メッセージを表示
      notifyAction({
        message: 'メトリクスが正常に更新されました',
        severity: 'success',
        duration: 3000
      });
    } catch (err) {
      console.error(`Error updating metric ${metricKey}:`, err);
      setError(`メトリクス "${metricKey}" の更新中にエラーが発生しました`);
      
      // 共通の通知コンポーネントを使用してエラーメッセージを表示
      notifyAction({
        message: `メトリクス "${metricKey}" の更新中にエラーが発生しました`,
        severity: 'error',
        duration: 5000
      });
    } finally {
      setUpdatingMetric(null);
    }
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>エラー</AlertTitle>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* 看護師ステータスセクション */}
          <Grid item xs={12}>
            <NurseStatusSection
              metricData={metrics?.nurse_status}
              isLoading={loading || updatingMetric === 'nurse_status'}
              onUpdate={handleUpdateMetric}
            />
          </Grid>

          {/* 患者ステータスセクション */}
          <Grid item xs={12}>
            <PatientStatusSection
              metricData={metrics?.patient_status}
              isLoading={loading || updatingMetric === 'patient_status'}
              onUpdate={handleUpdateMetric}
            />
          </Grid>

          {/* 対話ルームステータスセクション */}
          <Grid item xs={12}>
            <TalkRoomStatusSection
              metricData={metrics?.talk_room_status}
              isLoading={loading || updatingMetric === 'talk_room_status'}
              onUpdate={handleUpdateMetric}
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
export default Dashboard;
